import navigationConfig, { flattenNavigation } from "configs/NavigationConfig";
import urls from "urls";

/**
 * Welk menu-item hoort bij welke URL (BMX-22).
 *
 * Eén bron voor "waar ben ik in het menu", gebruikt door de zijbalk
 * (MenuContent) en de breadcrumb in de headerbalk. Die twee liepen uiteen:
 * de breadcrumb deed al langste-prefix-matching, de zijbalk vergeleek paden
 * op gelijkheid en stond daardoor op detailpagina's op het verkeerde item.
 *
 * Matching gebeurt op **padsegment**, niet op tekst: `/cms/purchase-orders/all`
 * hoort wel bij `/cms/purchase-orders/all/abc123` maar niet bij een pad als
 * `/cms/purchase-orders/allocations`. Vandaar `isUnder` en niet `startsWith`.
 *
 * Langste pad wint, zodat `/cms/reports/general` het menu-item van de
 * rapportagepagina pakt en niet de groep `/cms/reports` die erboven ligt.
 */

const stripTrailingSlash = (path) => (path && path.length > 1 ? path.replace(/\/+$/, "") : path || "");

/** Ligt `pathname` op of onder `base`? Alleen op een segmentgrens. */
const isUnder = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);

/**
 * Paden die niet onder hun eigen menu-item vallen en er dus naartoe gewezen
 * moeten worden. Alleen voor routes die écht bestaan (zie
 * views/app-views/**\/index.js); een alias naar een niet-bestaande route zou
 * stilletjes het verkeerde item highlighten.
 */
const PATH_ALIASES = [
  // Een inkooporder is via twee paden bereikbaar: het pad uit de navigatie
  // (/cms/purchase-orders/all/:id) en een ouder pad dat het meldingencentrum
  // en de factuurdetail nog gebruiken. Dat laatste valt onder /cms/orders en
  // zou anders bij Bestellingen uitkomen — of nergens.
  { prefix: "/cms/orders/purchase-orders", target: urls.purchaseOrdersList },
  // Product toevoegen en bewerken hebben geen eigen menu-item; die horen bij
  // de productenlijst.
  { prefix: urls.addProduct, target: urls.productList },
  { prefix: urls.editProduct, target: urls.productList },
];

/** Alle menu-items met een pad, langste pad eerst. */
const NAV_ITEMS = flattenNavigation()
  .filter((item) => Boolean(item.path))
  .map((item) => ({ item, matchPath: stripTrailingSlash(item.path) }))
  .sort((a, b) => b.matchPath.length - a.matchPath.length);

/** Het menu-item waar deze URL bij hoort, of null. */
export const findNavItemByPath = (pathname) => {
  const path = stripTrailingSlash(pathname || "");
  if (!path) return null;

  const direct = NAV_ITEMS.find((entry) => isUnder(path, entry.matchPath));
  if (direct) return direct.item;

  const alias = PATH_ALIASES.find((a) => isUnder(path, stripTrailingSlash(a.prefix)));
  if (!alias) return null;

  const target = NAV_ITEMS.find((entry) => entry.matchPath === stripTrailingSlash(alias.target));
  return target ? target.item : null;
};

/** Staat deze URL op het menu-item zelf, of op een pagina eronder (detail)? */
export const isNavDetailPath = (pathname) => {
  const item = findNavItemByPath(pathname);
  if (!item) return false;
  return stripTrailingSlash(pathname) !== stripTrailingSlash(item.path);
};

/**
 * De sleutels van de menu-items bóven `key`, van buiten naar binnen.
 *
 * Bewust door de boom gelopen in plaats van de sleutel op "-" te splitsen:
 * dat laatste leunt op de naamgeving en levert niet-bestaande sleutels op
 * (`shop-products-productList` geeft dan "shop", terwijl de groep "sales" heet).
 */
export const getNavAncestorKeys = (key) => {
  if (!key) return [];

  let found = [];
  const walk = (nodes, ancestors) =>
    (nodes || []).some((node) => {
      if (node.key === key) {
        found = ancestors;
        return true;
      }
      return walk(node.submenu, [...ancestors, node.key]);
    });

  walk(navigationConfig, []);
  return found;
};


/**
 * Sleutels van de items die in MenuContent als groepskop renderen (een
 * top-level item met submenu). Die zijn niet in- of uitklapbaar, dus ze horen
 * niet in `openKeys` van het menu thuis.
 */
export const NAV_GROUP_KEYS = navigationConfig
  .filter((item) => (item.submenu || []).length > 0)
  .map((item) => item.key);

/** De inklapbare submenu's die open moeten staan voor deze URL. */
export const getOpenNavKeys = (pathname) => {
  const [key] = getSelectedNavKeys(pathname);
  return getNavAncestorKeys(key).filter((ancestor) => !NAV_GROUP_KEYS.includes(ancestor));
};

/** Wat de zijbalk als geselecteerd moet aanzetten voor deze URL. */
export const getSelectedNavKeys = (pathname) => {
  const item = findNavItemByPath(pathname);
  return item ? [item.key] : [];
};
